/**
 * booking-state.js
 * -----------------
 * Single source of truth for the Naxarly booking wizard.
 *
 * Persists one JSON object to localStorage under the key "naxarly_booking"
 * and exposes a small, dependency-free API (window.Booking) that every
 * booking step can call to read or write that object safely.
 *
 * Usage (any booking step page/section):
 *   <script src="{{ 'booking-state.js' | asset_url }}" defer></script>
 *   ...
 *   var state = Booking.load();
 *   Booking.update('event.city', 'Madurai');
 *   Booking.addAddon({ id: 'extra-hour', name: 'Extra Hour', price: 1500 });
 *   var total = Booking.getTotal();
 *
 * Design notes:
 *   - localStorage is the source of truth. Every method reads the current
 *     persisted value first, so state is always consistent even across
 *     multiple tabs/pages, and no page has to hold stale in-memory state.
 *   - Every getter returns a deep copy. Mutating what you get back from
 *     Booking.load() (or any other method) never touches stored state —
 *     you must go through Booking.update()/save()/addAddon()/removeAddon().
 *   - Nothing here throws. Storage access, JSON parsing, and malformed
 *     shapes are all guarded, with safe fallbacks at every step.
 */
(function (window) {
  'use strict';

  var STORAGE_KEY = 'naxarly_booking';

  // ---------------------------------------------------------------------
  // Default shape
  // ---------------------------------------------------------------------
  function defaultState() {
    return {
      services:[],
      package: {
    key: '',
    name: '',
    packagePrice: 0,
    advancePrice: 0,
    variantId: 0
},
      event: {
        city: '',
        type: '',
        date: '',
        time: '',
        guests: ''
      },
      addons: [],
      customer: {
        name: '',
        phone: '',
        email: '',
        venue: '',
        notes: ''
      }
    };
  }

  // ---------------------------------------------------------------------
  // Safe storage layer
  // ---------------------------------------------------------------------
  // Wraps localStorage so a disabled/unavailable/full storage (private
  // browsing, sandboxed iframe, quota exceeded, etc.) degrades to an
  // in-memory fallback instead of throwing anywhere in the public API.
  var memoryFallback = {};
  var storage = (function () {
    try {
      var testKey = '__naxarly_storage_test__';
      window.localStorage.setItem(testKey, '1');
      window.localStorage.removeItem(testKey);
      return {
        get: function (key) {
          try {
            return window.localStorage.getItem(key);
          } catch (e) {
            return Object.prototype.hasOwnProperty.call(memoryFallback, key) ? memoryFallback[key] : null;
          }
        },
        set: function (key, value) {
          try {
            window.localStorage.setItem(key, value);
          } catch (e) {
            memoryFallback[key] = value;
          }
        }
      };
    } catch (e) {
      // localStorage is unavailable entirely — use an in-memory object
      // for the lifetime of the page so the API still works.
      return {
        get: function (key) {
          return Object.prototype.hasOwnProperty.call(memoryFallback, key) ? memoryFallback[key] : null;
        },
        set: function (key, value) {
          memoryFallback[key] = value;
        }
      };
    }
  })();

  // ---------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------
  function isPlainObject(val) {
    return !!val && typeof val === 'object' && !Array.isArray(val);
  }

  function deepClone(obj) {
    if (typeof window.structuredClone === 'function') {
      try {
        return window.structuredClone(obj);
      } catch (e) {
        // fall through to JSON clone
      }
    }
    try {
      return JSON.parse(JSON.stringify(obj));
    } catch (e) {
      return obj;
    }
  }

  // Recursively fills any missing keys in `target` using `defaults`,
  // without dropping unexpected extra keys already present in `target`.
  // This self-heals state that was saved under an older/partial shape.
  function deepMerge(defaults, target) {
    if (!isPlainObject(target)) return deepClone(defaults);

    var result = Array.isArray(defaults) ? [] : {};
    var key;

    for (key in defaults) {
      if (!Object.prototype.hasOwnProperty.call(defaults, key)) continue;
      var defaultVal = defaults[key];
      var targetVal = target[key];

      if (isPlainObject(defaultVal)) {
        result[key] = deepMerge(defaultVal, isPlainObject(targetVal) ? targetVal : {});
      } else if (Array.isArray(defaultVal)) {
        result[key] = Array.isArray(targetVal) ? deepClone(targetVal) : deepClone(defaultVal);
      } else {
        result[key] = targetVal !== undefined ? targetVal : defaultVal;
      }
    }

    // Preserve any extra keys the caller already had that aren't in the
    // default shape (e.g. forward-compatibility with a future field).
    for (key in target) {
      if (Object.prototype.hasOwnProperty.call(target, key) && !(key in result)) {
        result[key] = target[key];
      }
    }

    return result;
  }

  function setPath(obj, path, value) {
    if (typeof path !== 'string' || !path.length) return;
    var segments = path.split('.').filter(Boolean);
    var cursor = obj;

    for (var i = 0; i < segments.length - 1; i++) {
      var segment = segments[i];
      if (!isPlainObject(cursor[segment])) {
        cursor[segment] = {};
      }
      cursor = cursor[segment];
    }

    cursor[segments[segments.length - 1]] = value;
  }

  function generateAddonId(addon) {
    var base = (addon && addon.name ? String(addon.name) : 'addon')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    return (base || 'addon') + '-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
  }

  // ---------------------------------------------------------------------
  // Read / write the persisted object
  // ---------------------------------------------------------------------

  // Returns the *normalized* live object (not a clone) — for internal use
  // only. Any code outside this file should use Booking.load() instead,
  // which returns a safe deep copy.
  function readNormalized() {
    var raw = storage.get(STORAGE_KEY);
    var defaults = defaultState();

    if (!raw) {
      var fresh = defaults;
      persist(fresh);
      return fresh;
    }

    var parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      // Corrupted JSON — self-heal by resetting to defaults.
      persist(defaults);
      return defaults;
    }

    var merged = deepMerge(defaults, parsed);

    // Guard against addons not being an array (e.g. hand-edited storage).
    if (!Array.isArray(merged.addons)) {
      merged.addons = [];
    }

    return merged;
  }

  function persist(state) {
    try {
      storage.set(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      // Swallow — persistence best-effort, never crash the caller.
    }

    // Let any listening booking step react without polling localStorage.
    try {
      window.dispatchEvent(
        new CustomEvent('naxarly:booking:change', { detail: deepClone(state) })
      );
    } catch (e) {
      // CustomEvent unsupported or dispatch failed — non-critical.
    }
  }

  // ---------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------
  var Booking = {
    /**
     * Returns a deep copy of the current booking state.
     * Creates and persists the default shape first if nothing is stored
     * yet, and self-heals any state saved under an incomplete shape.
     */
    load: function () {
      return deepClone(readNormalized());
    },

    /**
     * Persists a full or partial state object, deep-merged onto the
     * current state (so you can pass just the piece you changed).
     * Returns a deep copy of the resulting, persisted state.
     *
     *   Booking.save({ event: { city: 'Madurai' } });
     *   Booking.save(); // re-persists/normalizes current state, no changes
     */
    save: function (partialState) {
      var current = readNormalized();
      var next = partialState ? deepMerge(current, partialState) : current;

      if (!Array.isArray(next.addons)) next.addons = [];

      persist(next);
      return deepClone(next);
    },

    /**
     * Sets a single field by dot-path and persists it.
     *   Booking.update('customer.name', 'Arun Kumar');
     *   Booking.update('package.price', 7999);
     * Returns a deep copy of the resulting state.
     */
    update: function (path, value) {
      var current = readNormalized();
      setPath(current, path, value);
      persist(current);
      return deepClone(current);
    },

    /**
     * Wipes booking state back to the default empty shape.
     * Returns a deep copy of the reset state.
     */
    reset: function () {
      var fresh = defaultState();
      persist(fresh);
      return deepClone(fresh);
    },

    /**
     * Adds an add-on. Expected shape: { id?, name, price, qty? }.
     * - If `id` is omitted, one is generated from the name.
     * - If an add-on with the same `id` already exists, its quantity is
     *   incremented (by the incoming qty, default 1) instead of creating
     *   a duplicate entry.
     * Returns a deep copy of the resulting state.
     */
    addAddon: function (addon) {
      var current = readNormalized();

      if (!isPlainObject(addon)) return deepClone(current);

      var normalized = {
        id: addon.id || generateAddonId(addon),
        name: addon.name || '',
        price: Number(addon.price) || 0,
        qty: Number(addon.qty) > 0 ? Number(addon.qty) : 1
      };

      var existing = null;
      for (var i = 0; i < current.addons.length; i++) {
        if (current.addons[i].id === normalized.id) {
          existing = current.addons[i];
          break;
        }
      }

      if (existing) {
        existing.qty = (Number(existing.qty) || 0) + normalized.qty;
      } else {
        current.addons.push(normalized);
      }

      persist(current);
      return deepClone(current);
    },

    /**
     * Removes an add-on by id. No-op (does not throw) if the id isn't found.
     * Returns a deep copy of the resulting state.
     */
    removeAddon: function (id) {
      var current = readNormalized();
      current.addons = current.addons.filter(function (a) {
        return a && a.id !== id;
      });
      persist(current);
      return deepClone(current);
    },

    /**
     * Returns the current estimated total as a number:
     *   package.price + sum(addon.price * addon.qty)
     */
    getTotal: function () {
      var current = readNormalized();
      var packagePrice = Number(current.package && current.package.price) || 0;

      var addonsTotal = current.addons.reduce(function (sum, addon) {
        var price = Number(addon && addon.price) || 0;
        var qty = Number(addon && addon.qty) || 1;
        return sum + price * qty;
      }, 0);

      return packagePrice + addonsTotal;
    },
    /**
 * Add a service to the booking.
 * If it already exists, replace it.
 */
addService: function(service){

    var current = readNormalized();

    if(!isPlainObject(service)){
        return deepClone(current);
    }

    if(!Array.isArray(current.services)){
        current.services = [];
    }

    current.services =
        current.services.filter(function(s){
            return s.id !== service.id;
        });

    current.services.push({

        id: service.id,

        package: service.package || ""

    });

    persist(current);

    return deepClone(current);

},

/**
 * Remove a selected service.
 */
removeService: function(id){

    var current = readNormalized();

    if(!Array.isArray(current.services)){
        current.services = [];
    }

    current.services =
        current.services.filter(function(s){

            return s.id !== id;

        });

    persist(current);

    return deepClone(current);

},

/**
 * Check whether a service is already selected.
 */
hasService: function(id){

    var current = readNormalized();

    if(!Array.isArray(current.services)){
        return false;
    }

    return current.services.some(function(s){

        return s.id === id;

    });

},

    /**
     * Sets the single "primary" selected service+package. `services` stays
     * the source of truth (today capped at one entry, ready to grow into
     * a real list later); `package` is a denormalized cache of the same
     * selection kept only to make rendering cheap. Both are written in the
     * same call so they can never disagree with each other.
     *
     * Expected shape (catalog entry, see booking-catalog.js):
     *   Booking.setPrimaryService({
     *     id: 'vr-games',        // service key
     *     package: 'starter',    // package key
     *     name: 'Starter Package',
     *     price: 599900,
     *     image: '',
     *     service: 'VR Games'    // display label, optional (defaults to id)
     *   });
     *
     * Returns a deep copy of the resulting state.
     */
    setPrimaryService: function (entry) {
      var current = readNormalized();

      if (!isPlainObject(entry) || !entry.id || !entry.package) {
        return deepClone(current);
      }

      current.services = [{ id: entry.id, package: entry.package }];
      current.package = {
        key: entry.package,
        service: entry.service || entry.id,
        name: entry.name || '',
        packagePrice: Number(entry.packagePrice) || 0,
advancePrice: Number(entry.advancePrice) || 0,
        image: entry.image || '',
        variantId: Number(entry.variantId) || 0
      };

      persist(current);
      return deepClone(current);
    },

    /**
     * Centralized navigation. Every page should redirect through this
     * instead of setting window.location.href directly, so redirect
     * behaviour lives in exactly one place. Safely no-ops on a falsy url.
     */
    navigate: function (url) {
      if (!url || typeof url !== 'string') return;
      window.location.href = url;
    }
  };

  window.Booking = Booking;
})(window);